import { useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import API from "../../common/API";

interface Proyecto {
    id: number;
    proyecto: string;
    provincia: string;
    municipio: string;
    constructora: string | null;
    fiduciaria: string | null;
    anio: number | null;
    fechaSalida: string | null;
    unidades: number | null;
    costoProyecto: number | null;
    aprobado: boolean | null;
}

interface Summary {
    proyectos: number;
    unidadesTotales: number;
    inversionTotal: number;
    primerAnio: number | null;
    ultimoAnio: number | null;
}

interface FideicomisoData {
    summary: Summary | null;
    proyectos: Proyecto[];
}

const formatCurrency = (value: number | null) =>
    value == null ? "-" : `RD$ ${value.toLocaleString("es-DO", { maximumFractionDigits: 0 })}`;

const RecordFideicomiso = (props: { rnc: string }) => {
    const { rnc } = props;
    const [data, setData] = useState<FideicomisoData | null>(null);
    const { t } = useTranslation();

    const bindRecords = async () => {
        try {
            const response = await API.getAction(`DGII/Fideicomiso/${rnc}`);
            if (response.status === 200 && response.data?.response?.summary) {
                setData(response.data.response);
            }
        } catch (error) {
            // silent fail — supplementary data
        }
    };

    useEffect(() => {
        if (rnc) bindRecords();
    }, [rnc]);

    if (!data?.summary) return null;

    const { summary, proyectos } = data;

    return (
        <Card className="my-4">
            <Card.Header className="display-4">
                {t("Proyectos de Fideicomiso (MIVHED)")}
            </Card.Header>
            <Card.Body className="p-4">
                <Row className="mb-3">
                    <Col xs={6} md={3}>
                        <b className="me-2">{t("Proyectos")}</b>
                        {summary.proyectos}
                    </Col>
                    <Col xs={6} md={3}>
                        <b className="me-2">{t("Unidades")}</b>
                        {summary.unidadesTotales}
                    </Col>
                    <Col xs={12} md={3}>
                        <b className="me-2">{t("Inversión Total")}</b>
                        {formatCurrency(summary.inversionTotal)}
                    </Col>
                    <Col xs={12} md={3}>
                        <b className="me-2">{t("Años")}</b>
                        {summary.primerAnio && summary.ultimoAnio
                            ? `${summary.primerAnio} - ${summary.ultimoAnio}`
                            : "-"}
                    </Col>
                </Row>

                <hr />
                <div className="table-responsive">
                    <table className="table table-sm table-hover">
                        <thead>
                            <tr>
                                <th>{t("Proyecto")}</th>
                                <th>{t("Municipio")}</th>
                                <th>{t("Fiduciaria")}</th>
                                <th>{t("Año")}</th>
                                <th>{t("Unidades")}</th>
                                <th>{t("Costo")}</th>
                                <th>{t("Estado")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {proyectos.map((item) => (
                                <tr key={item.id}>
                                    <td>{item.proyecto}</td>
                                    <td>{[item.municipio, item.provincia].filter(Boolean).join(", ") || "-"}</td>
                                    <td>{item.fiduciaria ?? "-"}</td>
                                    <td>{item.anio ?? "-"}</td>
                                    <td>{item.unidades ?? "-"}</td>
                                    <td>{formatCurrency(item.costoProyecto)}</td>
                                    <td>
                                        <span className={`badge ${item.aprobado ? "bg-success" : "bg-secondary"}`}>
                                            {item.aprobado ? t("Aprobado") : t("Pendiente")}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card.Body>
        </Card>
    );
};

export default RecordFideicomiso;
